'use client';

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import {
  getChatSettingsPublic,
  chatCreateConversation,
  chatGetMyConversations,
  chatGetConversation,
  chatSendMessage,
  chatUpdateGuestInfo,
  type ChatSettings,
  type ChatConversation,
  type ChatMessage,
} from '@/lib/api-client';

interface ChatContextValue {
  // Settings
  settings: ChatSettings | null;
  settingsLoaded: boolean;
  // State
  conversations: ChatConversation[];
  activeConversation: ChatConversation | null;
  messages: ChatMessage[];
  unreadCount: number;
  // Panel
  panelOpen: boolean;
  setPanelOpen: (open: boolean) => void;
  // Actions
  startConversation: (message: string, opts?: { subject?: string; deliveryMode?: string }) => Promise<void>;
  sendMessage: (body: string) => Promise<void>;
  selectConversation: (id: string) => Promise<void>;
  updateGuestInfo: (data: { guestEmail?: string; guestName?: string }) => Promise<void>;
  refreshConversation: () => Promise<void>;
  sendTyping: () => void;
  // Guest
  guestToken: string | null;
  // Loading
  sending: boolean;
  // Typing
  staffTyping: boolean;
}

const ChatContext = createContext<ChatContextValue | null>(null);

export function useChatContext() {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error('useChatContext must be used within ChatProvider');
  return ctx;
}

function getOrCreateGuestToken(): string {
  const key = 'stocksiqpro_guest_token';
  let token = localStorage.getItem(key);
  if (!token) {
    token = typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : 'xxxx-xxxx-xxxx-xxxx'.replace(/x/g, () => Math.floor(Math.random() * 16).toString(16));
    localStorage.setItem(key, token);
  }
  return token;
}

function getWsUrl(): string {
  if (typeof window === 'undefined') return '';
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  // In dev, API is on port 4000; in prod, same host via proxy
  const apiHost = process.env.NEXT_PUBLIC_API_URL
    ? new URL(process.env.NEXT_PUBLIC_API_URL).host
    : window.location.host;
  return `${proto}//${apiHost}/ws/chat`;
}

export function ChatProvider({ children }: { children: React.ReactNode }) {
  const { user, loading: authLoading, getToken } = useAuth();

  const [settings, setSettings] = useState<ChatSettings | null>(null);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [conversations, setConversations] = useState<ChatConversation[]>([]);
  const [activeConversation, setActiveConversation] = useState<ChatConversation | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [panelOpen, setPanelOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [guestToken, setGuestToken] = useState<string | null>(null);
  const [staffTyping, setStaffTyping] = useState(false);

  const wsRef = useRef<WebSocket | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const wsConnected = useRef(false);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const activeConvRef = useRef<string | null>(null);

  // Keep ref in sync
  useEffect(() => { activeConvRef.current = activeConversation?.id ?? null; }, [activeConversation?.id]);

  // Initialize guest token (client-side only)
  useEffect(() => {
    if (!user && !authLoading) {
      setGuestToken(getOrCreateGuestToken());
    } else if (user) {
      setGuestToken(null);
    }
  }, [user, authLoading]);

  // Load chat settings (with sessionStorage fallback for resilience)
  useEffect(() => {
    getChatSettingsPublic()
      .then((s) => {
        setSettings(s);
        setSettingsLoaded(true);
        try { sessionStorage.setItem('chat_settings', JSON.stringify(s)); } catch {}
      })
      .catch(() => {
        // On failure, use cached settings so bubble doesn't randomly disappear
        try {
          const cached = sessionStorage.getItem('chat_settings');
          if (cached) setSettings(JSON.parse(cached));
        } catch {}
        setSettingsLoaded(true);
      });
  }, []);

  // ── WebSocket connection ────────────────────────────────────────────────────

  const connectWs = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    const token = user ? getToken() : guestToken;
    if (!token) return;

    const param = user ? `token=${encodeURIComponent(token)}` : `guest=${encodeURIComponent(token)}`;
    const url = `${getWsUrl()}?${param}`;

    try {
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        wsConnected.current = true;
        // Join the active conversation room
        if (activeConvRef.current) {
          ws.send(JSON.stringify({ type: 'join', conversationId: activeConvRef.current }));
        }
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          handleWsMessage(msg);
        } catch {
          // ignore
        }
      };

      ws.onclose = () => {
        wsConnected.current = false;
        wsRef.current = null;
        // Reconnect after 3 seconds if panel is still open
        setTimeout(() => {
          if (panelOpen) connectWs();
        }, 3000);
      };

      ws.onerror = () => {
        ws.close();
      };
    } catch {
      // WebSocket not available, fall back to polling
    }
  }, [user, guestToken, getToken, panelOpen]);

  // Connect/disconnect WS when panel opens/closes
  useEffect(() => {
    if (panelOpen && settingsLoaded && settings?.enabled) {
      connectWs();
    } else {
      wsRef.current?.close();
      wsRef.current = null;
      wsConnected.current = false;
    }
    return () => {
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, [panelOpen, settingsLoaded, settings?.enabled, connectWs]);

  // Join conversation room when active conversation changes
  useEffect(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN && activeConversation) {
      wsRef.current.send(JSON.stringify({ type: 'join', conversationId: activeConversation.id }));
    }
    setStaffTyping(false);
  }, [activeConversation?.id]);

  // Handle incoming WebSocket messages
  const handleWsMessage = useCallback((msg: { type: string; [key: string]: unknown }) => {
    switch (msg.type) {
      case 'new_message': {
        const message = msg.message as ChatMessage;
        if (msg.conversationId === activeConvRef.current) {
          setMessages((prev) => {
            // Avoid duplicates
            if (prev.some((m) => m.id === message.id)) return prev;
            return [...prev, message];
          });
        }
        break;
      }

      case 'conversation_update': {
        const convId = msg.conversationId as string;
        setConversations((prev) =>
          prev.map((c) => c.id === convId ? { ...c, status: msg.status as string ?? c.status } : c),
        );
        if (activeConvRef.current === convId) {
          setActiveConversation((prev) => prev ? { ...prev, status: msg.status as string ?? prev.status } : prev);
        }
        break;
      }

      case 'typing': {
        if (msg.senderType === 'staff' && msg.conversationId === activeConvRef.current) {
          setStaffTyping(true);
          if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
          typingTimeoutRef.current = setTimeout(() => setStaffTyping(false), 3000);
        }
        break;
      }

      case 'stop_typing': {
        if (msg.senderType === 'staff') {
          setStaffTyping(false);
        }
        break;
      }

      case 'status_change': {
        setSettings((prev) => prev ? { ...prev, online: msg.online as boolean } : prev);
        break;
      }
    }
  }, []);

  // ── Polling fallback (when WS is not connected) ─────────────────────────────

  useEffect(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (!panelOpen || !activeConversation || wsConnected.current) return;

    const token = user ? null : guestToken;
    pollRef.current = setInterval(() => {
      chatGetConversation(activeConversation.id, token)
        .then((conv) => {
          if (conv.messages) setMessages(conv.messages);
          setActiveConversation(conv);
        })
        .catch(() => {});
    }, 5000);

    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [panelOpen, activeConversation?.id, user, guestToken]);

  // ── Poll unread count even when panel is closed ─────────────────────────────

  useEffect(() => {
    if (!settingsLoaded || !settings?.enabled) return;
    if (authLoading) return;
    if (panelOpen) return; // panel handles its own updates when open

    const token = user ? null : guestToken;
    // Only poll if user has a guest token or is logged in
    if (!user && !guestToken) return;

    const checkUnread = () => {
      chatGetMyConversations(token)
        .then((convs) => setConversations(convs))
        .catch(() => {});
    };
    checkUnread();
    const interval = setInterval(checkUnread, 30000); // every 30s
    return () => clearInterval(interval);
  }, [settingsLoaded, settings?.enabled, authLoading, user, guestToken, panelOpen]);

  // ── Load conversations when panel opens ─────────────────────────────────────

  useEffect(() => {
    if (!panelOpen || !settingsLoaded || !settings?.enabled) return;
    if (authLoading) return;

    const token = user ? null : guestToken;
    chatGetMyConversations(token)
      .then((convs) => {
        setConversations(convs);
        const open = convs.find((c) => c.status !== 'resolved' && c.status !== 'closed');
        if (open && !activeConversation) {
          selectConversation(open.id);
        }
      })
      .catch(() => {});
  }, [panelOpen, settingsLoaded, settings?.enabled, authLoading, user, guestToken]);

  // ── Actions ─────────────────────────────────────────────────────────────────

  const selectConversation = useCallback(async (id: string) => {
    const token = user ? null : guestToken;
    try {
      const conv = await chatGetConversation(id, token);
      setActiveConversation(conv);
      setMessages(conv.messages ?? []);
    } catch {
      // ignore
    }
  }, [user, guestToken]);

  const startConversation = useCallback(async (message: string, opts?: { subject?: string; deliveryMode?: string; guestEmail?: string }) => {
    setSending(true);
    try {
      const token = user ? null : guestToken;
      const conv = await chatCreateConversation({
        message,
        subject: opts?.subject,
        deliveryMode: opts?.deliveryMode,
        guestEmail: opts?.guestEmail,
      }, token);
      setConversations((prev) => [conv, ...prev]);
      await selectConversation(conv.id);
    } finally {
      setSending(false);
    }
  }, [user, guestToken, selectConversation]);

  const sendMessageFn = useCallback(async (body: string) => {
    if (!activeConversation) return;
    setSending(true);
    try {
      const token = user ? null : guestToken;
      const msg = await chatSendMessage(activeConversation.id, body, token);
      setMessages((prev) => {
        if (prev.some((m) => m.id === msg.id)) return prev;
        return [...prev, msg];
      });
      // Send stop_typing via WS
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'stop_typing' }));
      }
    } catch {
      // Conversation may have been deleted by admin — start a new one
      setActiveConversation(null);
      setMessages([]);
      setConversations((prev) => prev.filter((c) => c.id !== activeConversation.id));
      // Auto-create a new conversation with the failed message
      try {
        const token = user ? null : guestToken;
        const conv = await chatCreateConversation({ message: body }, token);
        setConversations((prev) => [conv, ...prev]);
        await selectConversation(conv.id);
      } catch { /* ignore — user can retry */ }
    } finally {
      setSending(false);
    }
  }, [activeConversation, user, guestToken, selectConversation]);

  const updateGuestInfoFn = useCallback(async (data: { guestEmail?: string; guestName?: string }) => {
    if (!activeConversation) return;
    const token = user ? null : guestToken;
    await chatUpdateGuestInfo(activeConversation.id, data, token);
  }, [activeConversation, user, guestToken]);

  const refreshConversation = useCallback(async () => {
    if (!activeConversation) return;
    await selectConversation(activeConversation.id);
  }, [activeConversation, selectConversation]);

  const sendTyping = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'typing' }));
    }
  }, []);

  // Unread count across all conversations
  const unreadCount = conversations.reduce((sum, c) => sum + (c.unreadCount ?? 0), 0);

  return (
    <ChatContext.Provider value={{
      settings, settingsLoaded,
      conversations, activeConversation, messages, unreadCount,
      panelOpen, setPanelOpen,
      startConversation, sendMessage: sendMessageFn,
      selectConversation, updateGuestInfo: updateGuestInfoFn,
      refreshConversation, sendTyping,
      guestToken, sending, staffTyping,
    }}>
      {children}
    </ChatContext.Provider>
  );
}
